import { queryOptions, useQuery } from "@tanstack/react-query";
import { api, type RangeSelection } from "@/lib/api";

/** Matches the hourly server sync: refetching faster would return the same rows. */
const REFRESH_MS = 5 * 60 * 1000;

/*
 * The reads as option objects, shared by the hooks and by the prefetcher (`lib/route-prefetch.ts`), so a read
 * started ahead of the page lands under exactly the key the page asks for.
 */
export const leaderboardQuery = (range: RangeSelection) =>
  queryOptions({ queryKey: ["leaderboard", range], queryFn: () => api.leaderboard(range), staleTime: REFRESH_MS });
export const newWinnersQuery = () => queryOptions({ queryKey: ["new-winners"], queryFn: api.newWinners, staleTime: REFRESH_MS });
export const prizeContestsQuery = () =>
  queryOptions({ queryKey: ["prize-contests"], queryFn: api.prizeContests, staleTime: REFRESH_MS });
export const strategistQuery = (periodIndex: number | undefined) =>
  queryOptions({
    queryKey: ["strategist", periodIndex ?? "current"],
    queryFn: () => api.strategist(periodIndex),
    staleTime: REFRESH_MS,
  });
/** Which creatives were made on the platform: one small list shared by every "AI" tag (`components/ai-badge.tsx`). */
export const aiConceptsQuery = () =>
  queryOptions({ queryKey: ["ai-concepts"], queryFn: api.aiConcepts, staleTime: 10 * 60 * 1000, gcTime: Infinity });
export const topVideosQuery = (range: RangeSelection, limit: number) =>
  queryOptions({ queryKey: ["top-videos", range, limit], queryFn: () => api.topVideos(range, limit), staleTime: REFRESH_MS });
export const editorDetailQuery = (editorId: number, range: RangeSelection) =>
  queryOptions({ queryKey: ["editor", editorId, range], queryFn: () => api.editorDetail(editorId, range), staleTime: REFRESH_MS });
export const creativeDetailQuery = (slug: string, range: RangeSelection) =>
  queryOptions({ queryKey: ["creative", slug, range], queryFn: () => api.creativeDetail(slug, range), staleTime: REFRESH_MS });
export const periodsQuery = () => queryOptions({ queryKey: ["periods"], queryFn: api.periods, staleTime: 60 * 60 * 1000 });

export function useLeaderboard(range: RangeSelection) {
  return useQuery({
    ...leaderboardQuery(range),
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    // Keeps the previous period's numbers on screen while a new range loads,
    // so switching ranges never blanks the page.
    placeholderData: (previous) => previous,
  });
}

/**
 * The ranking beyond the podium.
 *
 * "Load more" refetches with a larger limit rather than paging: the ranking is
 * capped at twenty, so the most a second fetch can cost is twenty rows, and
 * `placeholderData` keeps the first ten on screen while it lands. Real
 * pagination would buy nothing and add an offset to keep in sync with a range
 * that can change underneath it.
 */
export function useTopVideos(range: RangeSelection, limit: number) {
  return useQuery({ ...topVideosQuery(range, limit), placeholderData: (previous) => previous });
}

/** Not keyed by range: the shortlist is always the last week. */
export function useNewWinners() {
  return useQuery(newWinnersQuery());
}

/**
 * The signed-in editor's current pay period, for the card on their home page.
 *
 * Keyed by the editor so that an admin stepping from one persona to another
 * never sees the last one's numbers under the new one's name. Disabled without
 * an editor: the server has no summary for such a session and says so with a
 * 404, which is a red line in the console for a card that is not drawn.
 */
export function useMySummary(editorId: number | null) {
  return useQuery({
    queryKey: ["my-summary", editorId],
    queryFn: api.mySummary,
    enabled: editorId !== null,
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
  });
}

/** Not keyed by range either: each contest window is a fixed pair of dates. */
export function usePrizeContests() {
  return useQuery(prizeContestsQuery());
}

/**
 * The strategist's payout for one pay period. `undefined` means the one in
 * progress, which is also the only period the server will guess at.
 *
 * Only ever mounted for the roles allowed to ask: editors get a 403 from this
 * endpoint, and calling it on every leaderboard load would put a red line in
 * their console for a panel they cannot see.
 */
export function useStrategist(periodIndex: number | undefined) {
  return useQuery({
    ...strategistQuery(periodIndex),
    // Keeps the previous fortnight's figures up while the next one loads, so
    // stepping through periods never blanks the card.
    placeholderData: (previous) => previous,
  });
}

export function usePeriods() {
  return useQuery(periodsQuery());
}

export function useEditorDetail(editorId: number | null, range: RangeSelection) {
  return useQuery({
    ...editorDetailQuery(editorId ?? 0, range),
    enabled: editorId !== null,
    // Only the same editor's previous range: another editor's numbers under
    // this one's name, even for a moment, would be wrong.
    placeholderData: (previous, query) => (query?.queryKey[1] === editorId ? previous : undefined),
  });
}

export function useCreativeDetail(slug: string | undefined, range: RangeSelection) {
  return useQuery({
    ...creativeDetailQuery(slug ?? "", range),
    enabled: Boolean(slug),
    placeholderData: (previous, query) => (query?.queryKey[1] === slug ? previous : undefined),
  });
}

/**
 * The strategist roster (names, tags, bonus board), for anything that names a
 * strategist from their id. Changes rarely, so it is kept for ten minutes.
 */
export function useStrategists() {
  return useQuery({ queryKey: ["strategists"], queryFn: api.strategists, staleTime: 10 * 60_000 });
}

/** A strategist's name from their id, falling back to the id while the roster loads. */
export function useStrategistName(): (id: string) => string {
  const { data } = useStrategists();
  return (id: string) => data?.find((who) => who.id === id)?.name ?? id;
}
